// First KYC step for Account Type -> Business //
import React, { useEffect } from 'react';
import countryOptions from '../../utils/AllCountryList';
import { useFormik } from 'formik';
import * as Yup from "yup";
import clsx from 'clsx';
import Autocomplete from "react-google-autocomplete";
import { updateProfile } from '../../utils/Api';
import { inputValidator } from '../../hooks/validator';
import limits from '../../hooks/limitations';
import { projectedAmount, projectedFrequency } from '../../utils/projectedValues';
import Select from "react-select";
import { useMemo, useState } from "react";
const BusinessDetails = ({ data, forward, skiper }) => {

    // Setting initial form data from fetched data
    const initial = {
        payment_per_annum: data?.payment_per_annum || projectedFrequency[0],
        value_per_annum: data?.value_per_annum || projectedAmount[0],
        business_name: data?.business_name || '',
        business_nature: data?.business_nature || '',
        registration_number: data?.registration_number || '',
        registration_date: data?.registration_date || '',
        registration_country: data?.registration_country || '',
        business_address: data?.business_address || ''
    }
    // Yup validation schema for Business Details
    const businessSchema = Yup.object().shape({
        value_per_annum: Yup.string(),
        payment_per_annum: Yup.string(),
        business_name: Yup.string().min(limits.business_name.min).max(limits.business_name.max).required('Business Name is required').trim().notOneOf([" "]),
        business_nature: Yup.string().min(limits.business_nature.min).max(limits.business_nature.max).required('Business Nature is required').trim().notOneOf([" "]),
        registration_number: Yup.string().min(limits.registration_number.min).max(limits.registration_number.max).required('Registration Number is required'),
        registration_date: Yup.string()
            .matches(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format (YYYY-MM-DD required)")
            .test(
                "valid-date-range",
                "Date must be within the last 200 years and not in the future",
                (value) => {
                    if (!value) return false;

                    const date = new Date(value);
                    if (isNaN(date.getTime())) return false; // Invalid date

                    const today = new Date();
                    const minDate = new Date();
                    minDate.setFullYear(today.getFullYear() - 200);

                    return date >= minDate && date <= today;
                }
            )
            .required("Registration date is required"),
        registration_country: Yup.string().max(limits.country).required('Registration Country is required'),
        business_address: Yup.string().max(limits.address).required('Business Address is required').trim().notOneOf([" "])
    })

const [selectedRegistrationCountry, setSelectedRegistrationCountry] = useState(
    data?.registration_country
        ? { value: data.registration_country, label: data.registration_country }
        : null
);
const handleRegistrationCountryChange = (selectedOption) => {
    setSelectedRegistrationCountry(selectedOption);
    handleChange({ target: { name: "registration_country", value: selectedOption ? selectedOption.value : "" } });
};
useEffect(() => {
    if (data?.registration_country) {
        setSelectedRegistrationCountry({
            value: data.registration_country,
            label: data.registration_country
        });
    }
}, [data]);
    // formik setup for Business Details
    const { values, errors, touched, handleBlur, handleChange, handleSubmit, setValues } = useFormik({
        initialValues: initial,
        validationSchema: businessSchema,
        onSubmit: async (values) => {
            forward(values);
            await updateProfile(values).then(res => {
                if (res.code === "200") {
                    let user = JSON.parse(sessionStorage.getItem("remi-user-dt"))
                    let local = { ...res?.data, is_digital_Id_verified: user?.is_digital_Id_verified }
                    sessionStorage.removeItem("remi-user-dt")
                    sessionStorage.setItem("remi-user-dt", JSON.stringify(local))
                } else if (res.code === "400") {
                    toast.error(res.message, { position: "bottom-right", hideProgressBar: true, autoClose: 2000 })
                }
            }).catch((err) => {
                toast.error(err.message, { position: "bottom-right", autoClose: 2000, hideProgressBar: true })
            })
        }
    });
const countrySelectOptions = useMemo(() => {
    if (!countryOptions || countryOptions.length === 0) return [];
    return countryOptions.map((country) => ({
        value: country.name,
        label: country.name,
        code: country.code,
    }));
}, [countryOptions]);
    // function to handle Skip;
    const handleSkip = () => {
        handleSubmit(values)
        skiper();
    }

    useEffect(() => {
        const dtToday = new Date();
        const maxDate = dtToday.toISOString().split('T')[0]; // Today's date
        const minDate = new Date(dtToday.setFullYear(dtToday.getFullYear() - 200)).toISOString().split('T')[0];

        document.getElementById("registration_date")?.setAttribute('max', maxDate);
        document.getElementById("registration_date")?.setAttribute('min', minDate);
    }, []);

    useEffect(() => {
        setValues({
            payment_per_annum: data?.payment_per_annum || projectedFrequency[0],
            value_per_annum: data?.value_per_annum || projectedAmount[0],
            business_name: data?.business_name || '',
            business_nature: data?.business_nature || '',
            registration_number: data?.registration_number || '',
            registration_date: data?.registration_date || '',
            registration_country: data?.registration_country || '',
            business_address: data?.business_address || ''
        })
    }, [data]);

    const changeHandler = (e) => {
        inputValidator(e, handleChange)
    }

    return (
        <div>
            <section className="kyc">
                <form onSubmit={handleSubmit} noValidate className="single-recipient">
                    <div className="row each-row">
                        <h5>Business Details</h5>
                        <div className="col-md-6">
                            <div className="input_field">
                                <p className="get-text">Name of business<span style={{ color: 'red' }} >*</span></p>
                                <input
                                    type="text"
                                    name="business_name"
                                    id="business_name"
                                    value={values.business_name}
                                    onChange={changeHandler}
                                    maxLength={limits.business_name.max}
                                    className={clsx(
                                        'form-control bg-transparent',
                                        { 'is-invalid': touched.business_name && errors.business_name },
                                        { 'is-valid': touched.business_name && !errors.business_name }
                                    )}
                                    onBlur={handleBlur}
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="input_field">
                                <p className="get-text">Nature of business<span style={{ color: 'red' }} >*</span></p>
                                <input
                                    type="text"
                                    name="business_nature"
                                    id="business_nature"
                                    value={values.business_nature}
                                    onChange={changeHandler}
                                    maxLength={limits.business_nature.max}
                                    className={clsx(
                                        'form-control bg-transparent',
                                        { 'is-invalid': touched.business_nature && errors.business_nature },
                                        { 'is-valid': touched.business_nature && !errors.business_nature }
                                    )}
                                    onBlur={handleBlur}
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="input_field">
                                <p className="get-text">Registration number<span style={{ color: 'red' }} >*</span></p>
                                <input
                                    type="text"
                                    name="registration_number"
                                    id="registration_number"
                                    value={values.registration_number}
                                    onChange={changeHandler}
                                    maxLength={limits.registration_number.max}
                                    className={clsx(
                                        'form-control bg-transparent',
                                        { 'is-invalid': touched.registration_number && errors.registration_number },
                                        { 'is-valid': touched.registration_number && !errors.registration_number }
                                    )}
                                    onBlur={handleBlur}
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="input_field">
                                <p className="get-text">Registration date<span style={{ color: 'red' }} >*</span></p>
                                <input
                                    type="date"
                                    name="registration_date"
                                    id="registration_date"
                                    value={values.registration_date}
                                    onChange={handleChange}
                                    className={clsx(
                                        'form-control bg-transparent',
                                        { 'is-invalid': touched.registration_date && errors.registration_date },
                                        { 'is-valid': touched.registration_date && !errors.registration_date }
                                    )}
                                    onBlur={handleBlur}
                                />
                                {touched.registration_date && errors.registration_date && values.registration_date !== "" && (
                                    <div className='fv-plugins-message-container mt-1'>
                                        <div className='fv-help-block'>
                                            <span role='alert' className="text-danger">{errors.registration_date}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
           <div className="col-md-6">
    <div className="input_field">
        <p className="get-text">Registration country<span style={{ color: 'red' }}>*</span></p>
        <Select
            options={countrySelectOptions}
            value={selectedRegistrationCountry}
            onChange={handleRegistrationCountryChange}
            onBlur={() => handleBlur({ target: { name: "registration_country" } })}
            placeholder="Search country..."
            isClearable
            isSearchable
            noOptionsMessage={() => "No countries found"}
            styles={{
                control: (provided, state) => ({
                    ...provided,
                    backgroundColor: "rgba(252, 253, 255, 0.81)",
                    borderColor: touched.registration_country && errors.registration_country
                        ? "#dc3545"
                        : state.isFocused ? "#6414E9" : "#ced4da",
                    boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(100, 20, 233, 0.25)" : "none",
                    "&:hover": {
                        borderColor: touched.registration_country && errors.registration_country
                            ? "#dc3545" : "#6414E9",
                    },
                    minHeight: "38px",
                }),
                option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected ? "#6414E9" : state.isFocused ? "#f0e9ff" : "white",
                    color: state.isSelected ? "white" : "#212529",
                }),
                menu: (provided) => ({ ...provided, zIndex: 9999 }),
                placeholder: (provided) => ({ ...provided, color: "#6c757d" }),
                singleValue: (provided) => ({ ...provided, color: "#212529" }),
            }}
        />
        {touched.registration_country && errors.registration_country && (
            <div className="fv-plugins-message-container mt-1">
                <div className="fv-help-block">
                    <span role="alert" className="text-danger">{errors.registration_country}</span>
                </div>
            </div>
        )}
    </div>
</div>
                        <div className="col-md-12">
                            <div className="input_field">
                                <p className="get-text">Business address<span style={{ color: 'red' }} >*</span></p>
                                <Autocomplete
                                    apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
                                    placeholder="Street Address, Company or P.O. box.. "
                                    id="business_address"
                                    name="business_address"
                                    onBlur={handleBlur}
                                    className={
                                        clsx('form-control bg-transparent',
                                            { 'is-invalid': touched.business_address && errors.business_address },
                                            {
                                                'is-valid': touched.business_address && !errors.business_address
                                            }
                                        )
                                    }
                                    options={{
                                        types: [],
                                        componentRestrictions: { country: countryOptions.find(country => country.name === values?.registration_country)?.code?.toLowerCase() || "au" },
                                    }}
                                    onPlaceSelected={(e) => handleChange({ target: { name: "business_address", value: e.formatted_address } })}
                                    onChange={handleChange}
                                    value={values.business_address}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row each-row">
                        <p className="mb-3"><h5>Account Usage</h5><span className='small'>&nbsp;(Utilization above tier 1 requires additional verification documents.)</span></p>
                        <div className="col-md-6">
                            <div className="input_field">
                                <p className="get-text">Projected frequency of payments per annum
                                    {/* <span style={{ color: 'red' }} >*</span> */}
                                </p>
                                <input
                                    type="text"
                                    name="payment_per_anmum"
                                    value={values.payment_per_annum}
                                    disabled
                                    className={clsx(
                                        'form-control bg-transparent',
                                        { 'is-invalid': touched.payment_per_annum && errors.payment_per_annum && values.payment_per_annum !== "" && null },
                                        {
                                            'is-valid': touched.payment_per_annum && !errors.payment_per_annum && values.payment_per_annum !== "" && null,
                                        }
                                    )}
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="input_field">
                                <p className="get-text">Projected value of payments per annum
                                    {/* <span style={{ color: 'red' }} >*</span> */}
                                </p>
                                <input
                                    type="text"
                                    name="value_per_annum"
                                    value={values.value_per_annum}
                                    disabled
                                    className={clsx(
                                        'form-control bg-transparent',
                                        { 'is-invalid': touched.value_per_annum && errors.value_per_annum && values.value_per_annum !== "" && null },
                                        {
                                            'is-valid': touched.value_per_annum && !errors.value_per_annum && values.value_per_annum !== "" && null,
                                        }
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="next-step">
                        <button type="submit" className="login_button">Next <b>Step</b>  <img src="assets/img/home/Union.png" className="vission_image" alt="alt_image" /></button>
                        <button type="button" onClick={() => handleSkip()} className="SKip">Skip</button>
                    </div>

                </form>
            </section>
        </div>
    )
}

export default BusinessDetails